import { Controller } from "@hotwired/stimulus"
import { Loggable } from "./concerns/loggable"
import { CsrfTokenable } from "./concerns/csrf-tokenable"
import { ShowHide } from "./concerns/showHide";
import { Timestampable } from "./concerns/timestampable";

// Repeated shares of a post persist through the last player the user started.
const activeSessions = new Map()
const pendingSaves = new Map()
let resumingVisit = false

export default class extends Controller {

  static values = {
    postId: String,
    currentPosition: Number,
    lastServerPosition: Number,
    maxPosition: Number
  }

  static targets = ["returnToMaxPosition", "maxPositionTimestamp"]

  static outlets = ["video-timestamp", "share-w-timestamp"]

  saveIntervalMs = 30000
  navigationSaveTimeoutMs = 2000

  initialize() {
    Loggable(this, { debug: false })
    CsrfTokenable(this)
    ShowHide(this)
    Timestampable(this)
  }

  connect() {
    this.connectionGeneration = (this.connectionGeneration || 0) + 1
    this.toggleReturnToMaxPosition()
    this.refreshShare()
    this.log("connected")
  }

  activate() {
    if (!this.ownsSession()) this.lastSaveAt = Date.now()
    activeSessions.set(this.postIdValue, this)
  }

  disconnect() {
    const generation = this.connectionGeneration
    const save = this.updateServerWithCurrentPosition()
    Promise.resolve(save).finally(() => {
      if (generation === this.connectionGeneration && this.ownsSession()) activeSessions.delete(this.postIdValue)
    })
  }

  ownsSession() {
    return activeSessions.get(this.postIdValue) === this
  }

  tickUpdateSession(timeUpdatesCount, data){
    this.log("tickUpdateSession", timeUpdatesCount, data)

    this.updateDOMSession(data)

    if (this.ownsSession() && Date.now() - this.lastSaveAt >= this.saveIntervalMs){
      this.lastSaveAt = Date.now()
      this.updateServerWithCurrentPosition()
    }
    this.toggleReturnToMaxPosition()
  }

  toggleReturnToMaxPosition(){
    this.log("toggleReturnToMaxPosition", this.currentPositionValue, this.maxPositionValue)
    if (this.currentPositionValue < this.maxPositionValue){
      this.show(this.returnToMaxPositionTarget)
    } else {
      this.hide(this.returnToMaxPositionTarget)
    }
  }

  refreshMaxPosition(){
    if (this.currentPositionValue > this.maxPositionValue){
      this.maxPositionValue = this.currentPositionValue
      this.videoTimestampOutlet.timestampValue = this.secondsToTimestamp(this.maxPositionValue)
      this.maxPositionTimestampTarget.innerHTML = this.secondsToTimestamp(this.maxPositionValue)
    }
  }

  updateDOMSession(data){
    this.log("updateDOMSession", data)
    this.currentPositionValue = data.position
    this.refreshMaxPosition()
    this.refreshShare()
  }

  refreshShare(){
    if (this.currentPositionValue && this.hasShareWTimestampOutlet){
      this.shareWTimestampOutlet.timestampInputTarget.value = this.secondsToTimestamp(this.currentPositionValue)
    }
  }

  updateServerSession(data){
    if (!this.ownsSession()) return
    if (this.pendingSave && this.pendingPosition === data.position) return this.pendingSave

    // Serialize saves for the same post so a slower old request cannot win.
    const previous = pendingSaves.get(this.postIdValue) || Promise.resolve()
    const save = previous.then(async () => {
      if (!this.ownsSession()) return
      const response = await fetch(`/posts/${this.postIdValue}/post_open_session`, {
        method: "PUT",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({post_open_session: data})
      })
      if (!response.ok) throw new Error("Network response was not ok")
      await response.json()
      this.lastServerPositionValue = data.position
      this.lastSaveAt = Date.now()
    }).catch(error => {
      this.error(error)
    })
    this.pendingPosition = data.position
    this.pendingSave = save
    pendingSaves.set(this.postIdValue, save)
    save.finally(() => {
      if (pendingSaves.get(this.postIdValue) === save) pendingSaves.delete(this.postIdValue)
      if (this.pendingSave === save) this.pendingSave = null
    })
    return save
  }

  // called from action in html
  updateServerWithCurrentPosition(){
    this.log("updateServerWithCurrentPosition")
    if (this.ownsSession() && (this.currentPositionValue !== this.lastServerPositionValue || pendingSaves.has(this.postIdValue))){
      return this.updateServerSession({ position: this.currentPositionValue })
    }
  }

  beforeVisit(event) {
    if (resumingVisit || event.defaultPrevented) return

    for (const session of activeSessions.values()) session.updateServerWithCurrentPosition()
    const saves = Array.from(pendingSaves.values())
    if (saves.length === 0) return

    event.preventDefault()
    let timeoutId
    const timeout = new Promise(resolve => {
      timeoutId = setTimeout(resolve, this.navigationSaveTimeoutMs)
    })
    // Keep slow persistence from trapping the user on this page.
    Promise.race([Promise.all(saves), timeout]).then(() => {
      clearTimeout(timeoutId)
      resumingVisit = true
      try {
        Turbo.visit(event.detail.url)
      } finally {
        resumingVisit = false
      }
    })
  }

  destroySession(){
    if (!this.ownsSession()) return
    this.log("destroySession")
    this.lastServerPositionValue = this.currentPositionValue // para que no se actualice en el server
    activeSessions.delete(this.postIdValue)
    const previous = pendingSaves.get(this.postIdValue) || Promise.resolve()
    const deletion = previous.then(async () => {
      // A player started again while completion cleanup was waiting.
      if (activeSessions.has(this.postIdValue)) return
      const response = await fetch(`/posts/${this.postIdValue}/post_open_session`, {
        method: "DELETE",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })
      if (!response.ok) throw new Error("Network response was not ok")
      await response.json()
    }).catch(error => {
      this.error(error)
    })
    pendingSaves.set(this.postIdValue, deletion)
    deletion.finally(() => {
      if (pendingSaves.get(this.postIdValue) === deletion) pendingSaves.delete(this.postIdValue)
    })
    return deletion
  }

}
